'use client';

import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { useSession } from '@/providers/SessionProvider';
import { 
  GroceryStore, 
  UserGroceryList, 
  UserGroceryListItem,
  UserPriceAlert,
  GroceryProduct,
  StoreProduct 
} from '@/lib/grocery/models/types';

// ====================================
// TYPES
// ====================================

export interface ShoppingList {
  id: string;
  name: string;
  items: ShoppingListItem[];
  totalCost: number;
  estimatedSavings: number;
  optimizedStores: StoreRecommendation[];
  createdAt: Date;
  updatedAt: Date;
}

export interface ShoppingListItem {
  id: string;
  name: string;
  quantity: number;
  unitType?: string;
  unitSize?: number;
  estimatedPrice?: number;
  isCompleted: boolean;
  priority: number;
  notes?: string;
  product?: GroceryProduct;
  store?: GroceryStore;
  alternatives?: GroceryProduct[];
}

export interface PriceAlert {
  id: string;
  productId: string;
  product: GroceryProduct;
  storeId?: string;
  store?: GroceryStore;
  targetPrice: number;
  currentPrice: number;
  isActive: boolean;
  lastTriggered?: Date;
  createdAt: Date;
}

export interface StoreRecommendation {
  store: GroceryStore;
  totalCost: number;
  savings: number;
  savingsPercentage: number;
  itemAvailability: number;
  distance?: number;
  deliveryFee?: number;
  estimatedDeliveryTime?: string;
}

export interface SavingsData {
  totalSaved: number;
  monthlySavings: number;
  yearlySavings: number;
  savingsHistory: Array<{
    date: string;
    amount: number;
  }>;
  topSavingsCategories: Array<{
    category: string;
    amount: number;
    percentage: number;
  }>;
  priceDropAlerts: number;
  activeAlerts: number;
}

export interface SearchHistory {
  id: string;
  query: string;
  timestamp: Date;
  resultCount: number;
  filters?: Record<string, any>;
}

export interface GrocerySaverState {
  shoppingLists: ShoppingList[];
  priceAlerts: PriceAlert[];
  savingsAnalytics: SavingsData;
  preferredStores: GroceryStore[];
  recentSearches: SearchHistory[];
  isLoading: boolean;
  error: string | null;
  selectedStore?: GroceryStore;
  userPreferences: {
    maxDistance: number;
    preferDelivery: boolean;
    preferOrganic: boolean;
    budgetLimit?: number;
    notificationSettings: {
      priceDrops: boolean;
      newDeals: boolean;
      listReminders: boolean;
    };
  };
}

// ====================================
// ACTIONS
// ====================================

export type GrocerySaverAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_SHOPPING_LISTS'; payload: ShoppingList[] }
  | { type: 'ADD_SHOPPING_LIST'; payload: ShoppingList }
  | { type: 'UPDATE_SHOPPING_LIST'; payload: { id: string; updates: Partial<ShoppingList> } }
  | { type: 'DELETE_SHOPPING_LIST'; payload: string }
  | { type: 'SET_PRICE_ALERTS'; payload: PriceAlert[] }
  | { type: 'ADD_PRICE_ALERT'; payload: PriceAlert }
  | { type: 'UPDATE_PRICE_ALERT'; payload: { id: string; updates: Partial<PriceAlert> } }
  | { type: 'DELETE_PRICE_ALERT'; payload: string }
  | { type: 'SET_SAVINGS_ANALYTICS'; payload: SavingsData }
  | { type: 'SET_PREFERRED_STORES'; payload: GroceryStore[] }
  | { type: 'ADD_PREFERRED_STORE'; payload: GroceryStore }
  | { type: 'REMOVE_PREFERRED_STORE'; payload: string }
  | { type: 'SET_RECENT_SEARCHES'; payload: SearchHistory[] }
  | { type: 'ADD_RECENT_SEARCH'; payload: SearchHistory }
  | { type: 'SET_SELECTED_STORE'; payload: GroceryStore | undefined }
  | { type: 'UPDATE_USER_PREFERENCES'; payload: Partial<GrocerySaverState['userPreferences']> };

// ====================================
// REDUCER
// ====================================

const initialState: GrocerySaverState = {
  shoppingLists: [],
  priceAlerts: [],
  savingsAnalytics: {
    totalSaved: 0,
    monthlySavings: 0,
    yearlySavings: 0,
    savingsHistory: [],
    topSavingsCategories: [],
    priceDropAlerts: 0,
    activeAlerts: 0
  },
  preferredStores: [],
  recentSearches: [],
  isLoading: false,
  error: null,
  selectedStore: undefined,
  userPreferences: {
    maxDistance: 10, // km
    preferDelivery: false,
    preferOrganic: false,
    budgetLimit: undefined,
    notificationSettings: {
      priceDrops: true,
      newDeals: true,
      listReminders: true
    }
  }
};

function grocerySaverReducer(state: GrocerySaverState, action: GrocerySaverAction): GrocerySaverState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    
    case 'SET_SHOPPING_LISTS':
      return { ...state, shoppingLists: action.payload };
    
    case 'ADD_SHOPPING_LIST':
      return { 
        ...state, 
        shoppingLists: [...state.shoppingLists, action.payload] 
      };
    
    case 'UPDATE_SHOPPING_LIST':
      return {
        ...state,
        shoppingLists: state.shoppingLists.map(list =>
          list.id === action.payload.id
            ? { ...list, ...action.payload.updates, updatedAt: new Date() }
            : list
        )
      };
    
    case 'DELETE_SHOPPING_LIST':
      return {
        ...state,
        shoppingLists: state.shoppingLists.filter(list => list.id !== action.payload)
      };
    
    case 'SET_PRICE_ALERTS':
      return { ...state, priceAlerts: action.payload };
    
    case 'ADD_PRICE_ALERT':
      return { 
        ...state, 
        priceAlerts: [...state.priceAlerts, action.payload] 
      };
    
    case 'UPDATE_PRICE_ALERT':
      return {
        ...state,
        priceAlerts: state.priceAlerts.map(alert =>
          alert.id === action.payload.id
            ? { ...alert, ...action.payload.updates }
            : alert
        )
      };
    
    case 'DELETE_PRICE_ALERT':
      return {
        ...state,
        priceAlerts: state.priceAlerts.filter(alert => alert.id !== action.payload)
      };
    
    case 'SET_SAVINGS_ANALYTICS':
      return { ...state, savingsAnalytics: action.payload };
    
    case 'SET_PREFERRED_STORES':
      return { ...state, preferredStores: action.payload };
    
    case 'ADD_PREFERRED_STORE':
      return { 
        ...state, 
        preferredStores: [...state.preferredStores, action.payload] 
      };
    
    case 'REMOVE_PREFERRED_STORE':
      return {
        ...state,
        preferredStores: state.preferredStores.filter(store => store.id !== action.payload)
      };
    
    case 'SET_RECENT_SEARCHES':
      return { ...state, recentSearches: action.payload };
    
    case 'ADD_RECENT_SEARCH':
      return { 
        ...state, 
        recentSearches: [action.payload, ...state.recentSearches.slice(0, 9)] // Keep last 10
      };
    
    case 'SET_SELECTED_STORE':
      return { ...state, selectedStore: action.payload };
    
    case 'UPDATE_USER_PREFERENCES':
      return {
        ...state,
        userPreferences: { ...state.userPreferences, ...action.payload }
      };
    
    default:
      return state;
  }
}

// ====================================
// CONTEXT
// ====================================

interface GrocerySaverContextType {
  state: GrocerySaverState;
  dispatch: React.Dispatch<GrocerySaverAction>;
  // Shopping List Actions
  createShoppingList: (name: string, items?: ShoppingListItem[]) => Promise<void>;
  updateShoppingList: (id: string, updates: Partial<ShoppingList>) => Promise<void>;
  deleteShoppingList: (id: string) => Promise<void>;
  addItemToList: (listId: string, item: Omit<ShoppingListItem, 'id'>) => Promise<void>;
  removeItemFromList: (listId: string, itemId: string) => Promise<void>;
  toggleItemComplete: (listId: string, itemId: string) => Promise<void>;
  optimizeList: (listId: string) => Promise<void>;
  // Price Alert Actions
  createPriceAlert: (productId: string, targetPrice: number, storeId?: string) => Promise<void>;
  updatePriceAlert: (id: string, updates: Partial<PriceAlert>) => Promise<void>;
  deletePriceAlert: (id: string) => Promise<void>;
  // Store Actions
  addPreferredStore: (store: GroceryStore) => Promise<void>;
  removePreferredStore: (storeId: string) => Promise<void>;
  // Search Actions
  addSearchHistory: (query: string, resultCount: number, filters?: Record<string, any>) => Promise<void>;
  clearSearchHistory: () => Promise<void>;
  // Analytics
  refreshSavingsAnalytics: () => Promise<void>;
  // Preferences
  updatePreferences: (preferences: Partial<GrocerySaverState['userPreferences']>) => Promise<void>;
}

const GrocerySaverContext = createContext<GrocerySaverContextType | undefined>(undefined);

// ====================================
// PROVIDER
// ====================================

interface GrocerySaverProviderProps {
  children: ReactNode;
}

export function GrocerySaverProvider({ children }: GrocerySaverProviderProps) {
  const [state, dispatch] = useReducer(grocerySaverReducer, initialState);
  const { data: session } = useSession();

  // Load initial data when session is available
  useEffect(() => {
    if (session?.user?.id) {
      loadInitialData();
    }
  }, [session?.user?.id]);

  const loadInitialData = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      // Load shopping lists
      const listsResponse = await fetch('/api/grocery/lists');
      if (listsResponse.ok) {
        const lists = await listsResponse.json();
        dispatch({ type: 'SET_SHOPPING_LISTS', payload: lists });
      }

      // Load price alerts
      const alertsResponse = await fetch('/api/grocery/alerts');
      if (alertsResponse.ok) {
        const alerts = await alertsResponse.json();
        dispatch({ type: 'SET_PRICE_ALERTS', payload: alerts });
      }

      // Load savings analytics
      const analyticsResponse = await fetch('/api/grocery/analytics/savings');
      if (analyticsResponse.ok) {
        const analytics = await analyticsResponse.json();
        dispatch({ type: 'SET_SAVINGS_ANALYTICS', payload: analytics });
      }

      // Load preferred stores
      const storesResponse = await fetch('/api/grocery/stores/preferred');
      if (storesResponse.ok) {
        const stores = await storesResponse.json();
        dispatch({ type: 'SET_PREFERRED_STORES', payload: stores });
      }

      // Load recent searches
      const searchesResponse = await fetch('/api/grocery/search/history');
      if (searchesResponse.ok) {
        const searches = await searchesResponse.json();
        dispatch({ type: 'SET_RECENT_SEARCHES', payload: searches });
      }

    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: 'Failed to load grocery data' });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  // Shopping List Actions
  const createShoppingList = async (name: string, items: ShoppingListItem[] = []) => {
    try {
      const response = await fetch('/api/grocery/lists', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, items })
      });

      if (response.ok) {
        const newList = await response.json();
        dispatch({ type: 'ADD_SHOPPING_LIST', payload: newList });
      }
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: 'Failed to create shopping list' });
    }
  };

  const updateShoppingList = async (id: string, updates: Partial<ShoppingList>) => {
    try {
      const response = await fetch(`/api/grocery/lists/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });

      if (response.ok) {
        dispatch({ type: 'UPDATE_SHOPPING_LIST', payload: { id, updates } });
      }
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: 'Failed to update shopping list' });
    }
  };

  const deleteShoppingList = async (id: string) => {
    try {
      const response = await fetch(`/api/grocery/lists/${id}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        dispatch({ type: 'DELETE_SHOPPING_LIST', payload: id });
      }
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: 'Failed to delete shopping list' });
    }
  };

  const addItemToList = async (listId: string, item: Omit<ShoppingListItem, 'id'>) => {
    try {
      const response = await fetch(`/api/grocery/lists/${listId}/items`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(item)
      });

      if (response.ok) {
        const updatedList = await response.json();
        dispatch({ type: 'UPDATE_SHOPPING_LIST', payload: { id: listId, updates: updatedList } });
      }
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: 'Failed to add item to list' });
    }
  };

  const removeItemFromList = async (listId: string, itemId: string) => {
    try {
      const response = await fetch(`/api/grocery/lists/${listId}/items/${itemId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        const updatedList = await response.json();
        dispatch({ type: 'UPDATE_SHOPPING_LIST', payload: { id: listId, updates: updatedList } });
      }
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: 'Failed to remove item from list' });
    }
  };

  const toggleItemComplete = async (listId: string, itemId: string) => {
    try {
      const response = await fetch(`/api/grocery/lists/${listId}/items/${itemId}/toggle`, {
        method: 'PATCH'
      });

      if (response.ok) {
        const updatedList = await response.json();
        dispatch({ type: 'UPDATE_SHOPPING_LIST', payload: { id: listId, updates: updatedList } });
      }
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: 'Failed to toggle item completion' });
    }
  };

  const optimizeList = async (listId: string) => {
    try {
      const response = await fetch(`/api/grocery/lists/${listId}/optimize`, {
        method: 'POST'
      });

      if (response.ok) {
        const optimizedList = await response.json();
        dispatch({ type: 'UPDATE_SHOPPING_LIST', payload: { id: listId, updates: optimizedList } });
      }
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: 'Failed to optimize shopping list' });
    }
  };

  // Price Alert Actions
  const createPriceAlert = async (productId: string, targetPrice: number, storeId?: string) => {
    try {
      const response = await fetch('/api/grocery/alerts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ productId, targetPrice, storeId })
      });

      if (response.ok) {
        const newAlert = await response.json();
        dispatch({ type: 'ADD_PRICE_ALERT', payload: newAlert });
      }
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: 'Failed to create price alert' });
    }
  };

  const updatePriceAlert = async (id: string, updates: Partial<PriceAlert>) => {
    try {
      const response = await fetch(`/api/grocery/alerts/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });

      if (response.ok) {
        dispatch({ type: 'UPDATE_PRICE_ALERT', payload: { id, updates } });
      }
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: 'Failed to update price alert' });
    }
  };

  const deletePriceAlert = async (id: string) => {
    try {
      const response = await fetch(`/api/grocery/alerts/${id}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        dispatch({ type: 'DELETE_PRICE_ALERT', payload: id });
      }
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: 'Failed to delete price alert' });
    }
  };

  // Store Actions
  const addPreferredStore = async (store: GroceryStore) => {
    try {
      const response = await fetch('/api/grocery/stores/preferred', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ storeId: store.id })
      });

      if (response.ok) {
        dispatch({ type: 'ADD_PREFERRED_STORE', payload: store });
      }
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: 'Failed to add preferred store' });
    }
  };

  const removePreferredStore = async (storeId: string) => {
    try {
      const response = await fetch(`/api/grocery/stores/preferred/${storeId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        dispatch({ type: 'REMOVE_PREFERRED_STORE', payload: storeId });
      }
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: 'Failed to remove preferred store' });
    }
  };

  // Search Actions
  const addSearchHistory = async (query: string, resultCount: number, filters?: Record<string, any>) => {
    const search: SearchHistory = {
      id: Date.now().toString(),
      query,
      timestamp: new Date(),
      resultCount,
      filters
    };

    try {
      const response = await fetch('/api/grocery/search/history', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(search)
      });

      if (response.ok) {
        dispatch({ type: 'ADD_RECENT_SEARCH', payload: search });
      }
    } catch (error) {
      // Still add to local state even if API fails
      dispatch({ type: 'ADD_RECENT_SEARCH', payload: search });
    }
  };

  const clearSearchHistory = async () => {
    try {
      const response = await fetch('/api/grocery/search/history', {
        method: 'DELETE'
      });

      if (response.ok) {
        dispatch({ type: 'SET_RECENT_SEARCHES', payload: [] });
      }
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: 'Failed to clear search history' });
    }
  };

  // Analytics
  const refreshSavingsAnalytics = async () => {
    try {
      const response = await fetch('/api/grocery/analytics/savings');
      if (response.ok) {
        const analytics = await response.json();
        dispatch({ type: 'SET_SAVINGS_ANALYTICS', payload: analytics });
      }
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: 'Failed to refresh savings analytics' });
    }
  };

  // Preferences
  const updatePreferences = async (preferences: Partial<GrocerySaverState['userPreferences']>) => {
    try {
      const response = await fetch('/api/grocery/preferences', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(preferences)
      });

      if (response.ok) {
        dispatch({ type: 'UPDATE_USER_PREFERENCES', payload: preferences });
      }
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: 'Failed to update preferences' });
    }
  };

  const contextValue: GrocerySaverContextType = {
    state,
    dispatch,
    createShoppingList,
    updateShoppingList,
    deleteShoppingList,
    addItemToList,
    removeItemFromList,
    toggleItemComplete,
    optimizeList,
    createPriceAlert,
    updatePriceAlert,
    deletePriceAlert,
    addPreferredStore,
    removePreferredStore,
    addSearchHistory,
    clearSearchHistory,
    refreshSavingsAnalytics,
    updatePreferences
  };

  return (
    <GrocerySaverContext.Provider value={contextValue}>
      {children}
    </GrocerySaverContext.Provider>
  );
}

// ====================================
// HOOK
// ====================================

export function useGrocerySaver() {
  const context = useContext(GrocerySaverContext);
  if (context === undefined) {
    throw new Error('useGrocerySaver must be used within a GrocerySaverProvider');
  }
  return context;
} 